import type { InternalAxiosRequestConfig } from "axios";

// A data call with no bearer never leaves the browser. Auth and the constant
// tables below are the only paths that may.
export class NoSessionError extends Error {
  readonly code = "NO_SESSION" as const;
  constructor() {
    super("Not signed in");
    this.name = "NoSessionError";
  }
}

export interface SessionMemory {
  generation(): number;
  memory: { get(): string | null };
}

export type StampedConfig = {
  url?: string;
  __retried?: boolean;
  __veroGen?: number;
  __veroHadToken?: boolean;
};

const AUTH_PATH = /(^|\/)auth\//;
const PUBLIC_PATH =
  /(?:^|\/)(?:compute\/extractant-factors|compute\/behavior-percentiles|display-metadata)$|(?:^|\/)reference(?:\/|$)/;

function pathOf(url: string | undefined): string {
  return (url ?? "").split(/[?#]/)[0];
}

export function isAuthPath(url: string | undefined): boolean {
  return AUTH_PATH.test(pathOf(url));
}

export function allowsAnonymous(url: string | undefined): boolean {
  const path = pathOf(url);
  return AUTH_PATH.test(path) || PUBLIC_PATH.test(path);
}

export function stampRequest<T extends InternalAxiosRequestConfig>(
  cfg: T,
  session: SessionMemory,
): T {
  const token = session.memory.get();
  const stamped = cfg as T & StampedConfig;
  stamped.__veroGen = session.generation();
  stamped.__veroHadToken = Boolean(token);
  if (!token && !allowsAnonymous(cfg.url)) throw new NoSessionError();
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
}

// lose() only for the request that was actually sent: it carried a bearer, and
// logout/login has not moved the refresh generation since then.
export function requestStillCurrent(
  cfg: StampedConfig | undefined,
  generation: number,
): boolean {
  return !!cfg && cfg.__veroHadToken === true && cfg.__veroGen === generation;
}

export function shouldDropSession(
  cfg: StampedConfig | undefined,
  status: number | undefined,
  generation: number,
): boolean {
  return (
    status === 401 &&
    !!cfg &&
    !cfg.__retried &&
    !isAuthPath(cfg.url) &&
    requestStillCurrent(cfg, generation)
  );
}

export function firebaseRetryEligible(
  cfg: StampedConfig | undefined,
  status: number | undefined,
  generation: number,
  firebaseActive: boolean,
): cfg is StampedConfig {
  return firebaseActive && shouldDropSession(cfg, status, generation);
}

function isNetworkFailure(error: unknown): boolean {
  const code = (error as { code?: unknown } | null)?.code;
  return code === "auth/network-request-failed";
}

export async function takeFreshIdToken(
  sentGeneration: number | undefined,
  generationNow: () => number,
  lose: () => void,
  refresh: () => Promise<string | null>,
): Promise<string | null> {
  let token: string | null;
  try {
    token = await refresh();
  } catch (error) {
    // A refresh that could not reach Firebase is not an expired session.
    if (isNetworkFailure(error)) return null;
    if (sentGeneration === generationNow()) lose();
    return null;
  }
  if (sentGeneration !== generationNow()) return null;
  if (!token) {
    lose();
    return null;
  }
  return token;
}
